import React, { useState, useMemo } from "react";
import "../UIcss/Colors.css";
import Pagination from "./Pagination.jsx";
import CodeViewer from "./CodeViewer.jsx";
import { FaHashtag, FaPalette } from "react-icons/fa";
import { LuCopy, LuInfo } from "react-icons/lu";
import { colorStylesData } from "./colorStylesData.js";

// --- HELPER TO PARSE DATA ---
const getHexFromCSS = (css) => {
  const match = css.match(/#([A-Fa-f0-9]{6}|[A-Fa-f0-9]{3})/);
  return match ? match[0] : "";
};

const formatName = (key) => {
  return (
    key
      .replace("bg-", "")
      .replace("outline-", "")
      .split("-")
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
      .join(" ") + (key.includes("outline") ? " Outline" : "")
  );
};

// --- HELPER TO GET CODE ---
const getGeneratedCode = (color) => {
  const htmlCode = `<!DOCTYPE html>
<html lang="en">
<head>
    <meta charset="UTF-8">
    <meta name="viewport" content="width=device-width, initial-scale=1.0">
    <title>${color.name} - CodePulse UI</title>
    <style> 
        .color-box {
            width: 200px;
            height: 200px;
            border-radius: 20px;
            display: flex;
            align-items: center;
            justify-content: center;
            font-size: 1.2rem;
            font-weight: bold;
            box-shadow: 0 10px 30px rgba(0,0,0,0.5);
            ${color.css}
        }
    </style>
</head>
<body>
    <div class="color-box">
        ${color.hex || color.name}
    </div>
</body>
</html>`;

  return { html: htmlCode, css: color.css };
};

// --- RENDERER COMPONENT ---

const ColorRenderer = ({ color, onViewCode }) => {
  return (
    <div className="color-card">
      <div className="color-meta-header">
        <span className="color-label">{color.name}</span>
        <button
          className="color-copy-btn"
          onClick={(e) => {
            e.stopPropagation();
            onViewCode(color);
          }}
        >
          <LuCopy />
        </button>
      </div>

      <div className="color-preview-area">
        <div
          className="color-swatch"
          style={{
            backgroundColor: color.hex || "transparent",
            border: color.className.includes("outline")
              ? "2px solid currentColor"
              : "none",
            color: color.className.includes("outline") ? color.hex : "inherit",
          }}
        >
          {color.className.includes("outline") && (
            <div className="outline-indicator">Outline</div>
          )}
        </div>
      </div>

      <div className="color-footer-info">
        <FaHashtag size={12} />
        <span className="color-hex-text">{color.hex || "Gradient/Var"}</span>
      </div>
    </div>
  );
};

const Colors = () => {
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 40;

  const [viewerOpen, setViewerOpen] = useState(false);
  const [activeColor, setActiveColor] = useState(null);

  const colorList = useMemo(() => {
    return Object.keys(colorStylesData).map((key, index) => {
      const css = colorStylesData[key];
      const hex = getHexFromCSS(css);
      return {
        id: index + 1,
        className: key,
        name: formatName(key),
        hex: hex,
        css: css,
      };
    });
  }, []);

  const handlePageChange = (page) => setCurrentPage(page);

  const handleViewCode = (color) => {
    setActiveColor(color);
    setViewerOpen(true);
  };

  const currentItems = useMemo(() => {
    const indexOfLastItem = currentPage * itemsPerPage;
    const indexOfFirstItem = indexOfLastItem - itemsPerPage;
    return colorList.slice(indexOfFirstItem, indexOfLastItem);
  }, [currentPage, colorList]);

  const generatedCode = useMemo(() => {
    if (!activeColor) return { html: "", css: "" };
    return getGeneratedCode(activeColor);
  }, [activeColor]);

  return (
    <div className="colors-page-wrapper">
      <div className="library-header">
        <div className="header-content">
          <h1>
            <FaPalette /> Colors <span>Library</span>
          </h1>
        </div>
      </div>

      <div className="colors-grid">
        {currentItems.map((color) => (
          <ColorRenderer
            key={color.id}
            color={color}
            onViewCode={handleViewCode}
          />
        ))}
      </div>

      <Pagination
        currentPage={currentPage}
        totalPages={Math.ceil(colorList.length / itemsPerPage)}
        onPageChange={handlePageChange}
        totalItems={colorList.length}
        itemsPerPage={itemsPerPage}
      />

      {viewerOpen && activeColor && (
        <CodeViewer
          isOpen={viewerOpen}
          onClose={() => setViewerOpen(false)}
          componentName={activeColor.name}
          jsxCode={generatedCode.html}
          cssCode={generatedCode.css}
        />
      )}
    </div>
  );
};

export default Colors;
